import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { fetchClosedWeeks, fetchAree } from '../../lib/api';
import { TrendingUp, CheckCircle2, ListChecks, Trophy, Loader2 } from 'lucide-react';
import { AREE as AREE_DEFAULT } from '../../constants/areas';

/**
 * ClosedWeeksDetailModal — modale "dettaglio trend" che si apre al click
 * sulla sparkline nel CoachStatePanel.
 *
 * Mostra:
 *  - filtro per area utente (Tutte / Business / Lavoro / Personale / Salute / ...)
 *  - grafico a barre 26 settimane (più grande dello sparkline)
 *  - tabella per-settimana con breakdown MIT/Azioni/Totale
 *  - highlight della "best-week" (max total)
 *  - summary KPI: media settimanale, best week, ultimo confronto vs settimana precedente
 */
export default function ClosedWeeksDetailModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState(null); // null = tutte
  const [aree, setAree] = useState(null);

  // Carica la lista aree utente UNA VOLTA al primo open
  useEffect(() => {
    if (!open || aree !== null) return;
    fetchAree()
      .then((d) => setAree(Array.isArray(d) ? d : []))
      .catch(() => setAree([]));
  }, [open, aree]);

  // Carica i dati trend ogni volta che cambia filtro o si apre
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    fetchClosedWeeks(26, areaFilter)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { /* notifier globale */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, areaFilter]);

  const stats = useMemo(() => {
    if (!data?.weeks?.length) return null;
    const weeks = data.weeks;
    const totals = weeks.map((w) => w.total);
    const sum = totals.reduce((a, b) => a + b, 0);
    const avg = sum / weeks.length;
    const bestIdx = totals.reduce((iMax, v, i, arr) => (v > arr[iMax] ? i : iMax), 0);
    const best = weeks[bestIdx];
    const last = weeks[weeks.length - 1];
    const prev = weeks[weeks.length - 2];
    const deltaPrev = prev ? last.total - prev.total : null;
    return { sum, avg, best, bestIdx, last, deltaPrev };
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="closed-weeks-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <TrendingUp size={18} className="text-ok" />
            Trend completamenti · 26 settimane
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-soft">
            MIT chiuse (per data) + Azioni settimanali completate (per settimana ISO)
          </DialogDescription>
        </DialogHeader>

        {/* Filtro per area */}
        <AreaFilterChips
          aree={aree}
          value={areaFilter}
          onChange={setAreaFilter}
        />

        {loading && (
          <div className="flex items-center justify-center py-10 text-mute" data-testid="modal-loading">
            <Loader2 size={20} className="animate-spin mr-2" />
            <span className="font-mono text-sm">Carico…</span>
          </div>
        )}

        {!loading && stats && data && (
          <div className="space-y-5">
            {/* KPI strip */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <KpiCell
                icon={<CheckCircle2 size={14} className="text-ok" />}
                label="Questa settimana"
                value={stats.last.total}
                hint={stats.deltaPrev !== null ? `${stats.deltaPrev >= 0 ? '+' : ''}${stats.deltaPrev} vs scorsa` : null}
                tone={stats.deltaPrev >= 0 ? 'pos' : 'neg'}
                testid="kpi-this-week"
              />
              <KpiCell
                icon={<ListChecks size={14} className="text-soft" />}
                label="Media / settimana"
                value={stats.avg.toFixed(1)}
                hint={`${stats.sum} totali`}
                testid="kpi-avg"
              />
              <KpiCell
                icon={<Trophy size={14} className="text-amber-600" />}
                label="Settimana migliore"
                value={stats.best.total}
                hint={stats.best.label.slice(-3)}
                testid="kpi-best"
              />
            </div>

            {/* Bar chart */}
            <BarChart weeks={data.weeks} bestIdx={stats.bestIdx} />

            {/* Tabella per-settimana */}
            <div className="border border-line max-h-[260px] overflow-y-auto" data-testid="modal-table">
              <table className="w-full font-mono text-xs">
                <thead className="bg-tint sticky top-0">
                  <tr className="text-mute">
                    <th className="text-left px-3 py-2 font-normal uppercase tracking-wider">Settimana</th>
                    <th className="text-left px-2 py-2 font-normal uppercase tracking-wider">Periodo</th>
                    <th className="text-right px-2 py-2 font-normal uppercase tracking-wider">MIT</th>
                    <th className="text-right px-2 py-2 font-normal uppercase tracking-wider">Azioni</th>
                    <th className="text-right px-3 py-2 font-normal uppercase tracking-wider">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.weeks.slice().reverse().map((w, ri) => {
                    const isBest = w.label === stats.best.label;
                    const isLast = w.label === stats.last.label;
                    return (
                      <tr
                        key={w.label}
                        className={`border-t border-linesoft ${isBest ? 'bg-amber-50' : ''} ${isLast ? 'font-semibold' : ''}`}
                        data-testid={`modal-row-${w.label}`}
                      >
                        <td className="px-3 py-1.5 tabular-nums">
                          {w.label}
                          {isLast && <span className="ml-1 text-[10px] text-ok">·oggi</span>}
                          {isBest && <Trophy size={10} className="inline ml-1 text-amber-600" />}
                        </td>
                        <td className="px-2 py-1.5 text-mute">{formatRange(w.start, w.end)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{w.mit}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{w.azioni}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{w.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !stats && (
          <div className="py-10 text-center text-mute font-mono text-sm">
            Nessun dato disponibile.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AreaFilterChips({ aree, value, onChange }) {
  // Costruisci la lista: usa aree-utente se disponibili, fallback a costanti default.
  // L'ordine è: Tutte (always first), poi le aree del DB (ordinate per `ordine`).
  const list = Array.isArray(aree) && aree.length > 0
    ? aree.slice().sort((a, b) => (a.ordine ?? 99) - (b.ordine ?? 99))
    : Object.entries(AREE_DEFAULT).map(([key, v]) => ({ key, label: v.label, color: v.color }));

  return (
    <div className="flex flex-wrap items-center gap-1.5 -mt-1" data-testid="area-filter-chips">
      <Chip
        active={value === null}
        onClick={() => onChange(null)}
        testid="area-chip-all"
      >
        Tutte
      </Chip>
      {list.map((a) => (
        <Chip
          key={a.key}
          active={value === a.key}
          color={a.color}
          onClick={() => onChange(a.key)}
          testid={`area-chip-${a.key}`}
        >
          {a.label}
        </Chip>
      ))}
    </div>
  );
}

function Chip({ active, color, onClick, children, testid }) {
  const style = active && color ? { borderColor: color, color: color, backgroundColor: `${color}14` } : {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
        active
          ? 'border-ink text-ink bg-linesoft'
          : 'border-line text-soft hover:border-mute hover:text-ink'
      }`}
      style={style}
    >
      {children}
    </button>
  );
}

function KpiCell({ icon, label, value, hint, tone, testid }) {
  const toneClass = tone === 'pos' ? 'text-ok' : tone === 'neg' ? 'text-risk' : 'text-soft';
  return (
    <div className="border border-line px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-wider text-mute">{label}</span>
      </div>
      <div className="font-display text-2xl tabular-nums">{value}</div>
      {hint && <div className={`font-mono text-[10px] ${toneClass} tabular-nums`}>{hint}</div>}
    </div>
  );
}

function BarChart({ weeks, bestIdx }) {
  const w = 600;
  const h = 140;
  const padBottom = 18; // spazio per labels asse
  const padTop = 6;
  const usableH = h - padBottom - padTop;
  const maxVal = Math.max(1, ...weeks.map((x) => x.total));
  const barGap = 3;
  const barW = (w - (weeks.length - 1) * barGap) / weeks.length;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="modal-barchart" preserveAspectRatio="none">
      {/* gridline orizzontale al 50% e 100% */}
      <line x1="0" x2={w} y1={padTop} y2={padTop} stroke="var(--qp-border)" strokeWidth="0.5" />
      <line x1="0" x2={w} y1={padTop + usableH / 2} y2={padTop + usableH / 2} stroke="var(--qp-border-soft)" strokeWidth="0.5" strokeDasharray="2 2" />
      <line x1="0" x2={w} y1={padTop + usableH} y2={padTop + usableH} stroke="var(--qp-border)" strokeWidth="0.5" />

      {weeks.map((wk, i) => {
        const x = i * (barW + barGap);
        const bh = (wk.total / maxVal) * usableH;
        const y = padTop + usableH - bh;
        const isBest = i === bestIdx;
        const isLast = i === weeks.length - 1;
        const fill = isBest ? 'rgb(245 158 11)' : isLast ? 'rgb(5 150 105)' : 'rgb(16 185 129 / 0.55)';
        // Label sotto: ogni 4 settimane per evitare clutter
        const showLabel = i % 4 === 0 || isLast;
        return (
          <g key={wk.label}>
            <rect x={x} y={y} width={barW} height={bh} fill={fill} rx="1">
              <title>{`${wk.label} · MIT ${wk.mit} · Azioni ${wk.azioni} · Totale ${wk.total}`}</title>
            </rect>
            {showLabel && (
              <text
                x={x + barW / 2}
                y={h - 4}
                textAnchor="middle"
                fontSize="9"
                fontFamily="ui-monospace, monospace"
                fill="var(--qp-text-mute)"
              >
                {wk.label.slice(-3)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function formatRange(start, end) {
  // "08–14 Giu" / "29 Giu–05 Lug"
  try {
    const s = new Date(start);
    const e = new Date(end);
    const dayS = s.getDate().toString().padStart(2, '0');
    const dayE = e.getDate().toString().padStart(2, '0');
    const MESI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    if (s.getMonth() === e.getMonth()) {
      return `${dayS}–${dayE} ${MESI[s.getMonth()]}`;
    }
    return `${dayS} ${MESI[s.getMonth()]}–${dayE} ${MESI[e.getMonth()]}`;
  } catch {
    return `${start} → ${end}`;
  }
}
